import { useEffect, useState } from 'react'
import type { AuditionStage } from '@/lib/audition/state-machine'
import type { SubmissionAddress } from '@/lib/audition/submission-address'
import { trpc } from '@/lib/trpc'

const POLL_MS = 4_000
// A webhook that has not landed by then is a support case, not a spinner:
// the stage still flips server-side and the email still goes out.
const MAX_MS = 90_000

/**
 * Watches `audition.stage` after a payment or a booking until the webhook
 * has moved the applicant to one of `until` (LG-1033).
 *
 * Polls its own small query rather than `audition.get`: the wizard keeps
 * `get` mounted (disabled) for resume, and a refetch there would carry the
 * fresh completion onto the returning-applicant screen mid-checkout.
 *
 * `stalled` is terminal: once the watch has given up — the deadline passed
 * or a poll failed — it stays given up for the life of the watch.
 */
export function useStageWatch({
  address,
  active,
  until,
}: {
  address: SubmissionAddress
  active: boolean
  until: readonly AuditionStage[]
}): { reached: boolean; stalled: boolean } {
  // Giving up is terminal and has one clock: the timer below. An elapsed
  // check in the render path started a second clock from a different
  // instant (a discarded render, StrictMode's double render), so the poll
  // could outlive the message or stop before it. `refetchInterval` now
  // reads this flag rather than re-deriving the deadline from `Date.now()`.
  //
  // It never clears once set. A poll that recovers still reaches success
  // through `reached`, which every caller renders ahead of `stalled`;
  // un-latching would only flip the interim copy back and forth on one
  // dropped request, which reads as broken rather than as reassuring.
  const [gaveUp, setGaveUp] = useState(false)

  const query = trpc.audition.stage.useQuery(address, {
    enabled: active,
    // The give-up clock below counts wall time, but React Query pauses
    // `refetchInterval` while the tab is hidden. An applicant who switches
    // to their inbox to look for the receipt — the obvious thing to do
    // here — would come back to "taking a moment" having spent the whole
    // ninety seconds without a single poll. This makes the two clocks
    // agree; the request count is unchanged (23 at most, against a bucket
    // of 90).
    refetchIntervalInBackground: true,
    refetchInterval: (q) => {
      const stage = q.state.data?.stage
      if (stage && until.includes(stage)) return false
      if (gaveUp) return false
      return POLL_MS
    },
  })

  // The give-up clock runs on its own timer rather than off `dataUpdatedAt`:
  // that would never fire when every poll fails, which is exactly the case
  // the message exists for.
  useEffect(() => {
    if (!active) return
    const timer = setTimeout(() => setGaveUp(true), MAX_MS)
    return () => clearTimeout(timer)
  }, [active])

  // A failing poll gives up straight away: retrying for ninety seconds
  // would only show a spinner over an error the applicant cannot act on.
  const failing = query.isError
  useEffect(() => {
    if (active && failing) setGaveUp(true)
  }, [active, failing])

  const stage = query.data?.stage
  const reached = !!stage && until.includes(stage)
  const stalled = active && !reached && gaveUp
  return { reached, stalled }
}
